// src/components/RegisterCompetitionDialog.js
import { API_URL } from '../config/apiConfig';
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Typography,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    DialogContentText,
    Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { registerForCompetition } from '../api/competitionApi';
import axios from 'axios';


export default function RegisterCompetitionDialog({ open, onClose, competition, userId, onSuccess }) {
    const [formData, setFormData] = useState({
        teamName: '',
        category: '',
        teamMembers: competition?.isTeamCompetition ? [userId] : []
    });
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const searchUsers = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setUserSearchResults([]);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/search-users`, {
                params: { q: searchTerm },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setUserSearchResults(response.data);
        } catch (error) {
            console.error('Error searching users:', error);
        }
    };

    const handleSubmit = async () => {
        if (!formData.category) {
            setAlertMessage('Please select a category');
            setAlertOpen(true);
            return;
        }

        if (competition.isTeamCompetition) {
            if (!formData.teamName) {
                setAlertMessage('Please enter a team name');
                setAlertOpen(true);
                return;
            }
            if (formData.teamMembers.length !== competition.teamSize) {
                setAlertMessage(`Please add exactly ${competition.teamSize} team members`);
                setAlertOpen(true);
                return;
            }
        }

        try {
            setLoading(true);
            setErrorMessage('');
            await registerForCompetition(competition.id, {
                userId: competition.isTeamCompetition ? null : userId,
                teamName: competition.isTeamCompetition ? formData.teamName : null,
                category: formData.category,
                teamMembers: competition.isTeamCompetition ? formData.teamMembers : null
            });
            onSuccess();
        } catch (error) {
            console.error('Error registering:', error);
            setErrorMessage(error.response?.data?.error || 'Failed to register for competition');
        } finally {
            setLoading(false);
        }
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        setAlertMessage('');
    };

    const handleTeamMemberChange = (index, value) => {
        const newTeamMembers = [...formData.teamMembers];
        newTeamMembers[index] = value?.id || null;
        setFormData(prev => ({
            ...prev,
            teamMembers: newTeamMembers
        }));
    };

    const getCurrentUserName = () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'You';
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Register for {competition?.name}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers >
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {competition?.isTeamCompetition ? (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    This is a team competition. Please enter your team name and add {competition.teamSize} team members.
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Team Name *"
                                    value={formData.teamName}
                                    onChange={(e) => setFormData(prev => ({ ...prev, teamName: e.target.value }))}
                                    placeholder="Enter a unique team name"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Team Members
                                </Typography>
                            </Grid>

                            {/* First member is always the current user */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Member 1 (You)"
                                    value={getCurrentUserName()}
                                    disabled
                                />
                            </Grid>

                            {/* Additional team members */}
                            {Array.from({ length: competition.teamSize - 1 }, (_, index) => (
                                <Grid item xs={12} key={index}>
                                    <Autocomplete
                                        options={userSearchResults}
                                        getOptionLabel={(option) => `${option.fullName}  (${option.email})`}
                                        onChange={(e, value) => handleTeamMemberChange(index + 1, value)}
                                        onInputChange={(e, value) => searchUsers(value)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={`Member ${index + 2} *`}
                                                placeholder="Search for a member"
                                            />
                                        )}
                                    />
                                </Grid>
                            ))}
                        </>
                    ) : (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Register for this individual competition by selecting your category.
                            </Typography>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <FormControl fullWidth required>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.category}
                                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                label="Category"
                            >
                                {competition?.categories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Registering...' : 'Register'}
                </Button>
            </DialogActions>

            {/* Alert Dialog */}
            <Dialog
                open={alertOpen}
                onClose={handleAlertClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="alert-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Warning
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="alert-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleAlertClose}
                        color="primary"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}